import React, { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import {
    useTheme,
    Typography,
    IconButton,
    CircularProgress,
    Theme,
    TextField,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import AssessmentIcon from '@material-ui/icons/Assessment'
import {
    VendorProjectAnalytics,
    VendorProfitTrends,
} from '../../hooks/useEZFolioProject'

const MAX_CHART_PROJECTS = 15

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number.isFinite(value) ? value : 0)

const toNumber = (value: number | undefined | null) =>
    Number.isFinite(Number(value)) ? Number(value) : 0

const projectLabel = (project: VendorProjectAnalytics) => {
    const name = project.name?.trim() || 'Project'
    const apt = project.apartment_name?.trim()
    return apt ? `${apt} — ${name}` : name
}

interface Props {
    projects: VendorProjectAnalytics[]
    loading?: boolean
    trends?: VendorProfitTrends
    trendsLoading?: boolean
    trendsStartDate?: string
    trendsEndDate?: string
    onTrendsStartDateChange?: (value: string) => void
    onTrendsEndDateChange?: (value: string) => void
    onClose?: () => void
    /** When true, renders inline without overlay header/close controls. */
    embedded?: boolean
}

const MetricCard: React.FC<{
    label: string
    value: string | number
    color?: string
}> = ({ label, value, color }) => {
    const theme = useTheme()
    return (
        <div
            style={{
                textAlign: 'center',
                padding: theme.spacing(1.5),
                backgroundColor: theme.palette.grey[50],
                borderRadius: 6,
                borderLeft: `4px solid ${color || theme.palette.primary.main}`,
            }}
        >
            <Typography variant="body2" color="textSecondary">
                {label}
            </Typography>
            <Typography
                variant="h5"
                style={{ fontWeight: 700, color: color || 'inherit' }}
            >
                {value}
            </Typography>
        </div>
    )
}

const chartCardStyle = (theme: Theme): React.CSSProperties => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    padding: theme.spacing(2),
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
})

export const VendorProfitCharts: React.FC<Props> = ({
    projects,
    loading,
    trends = {},
    trendsLoading = false,
    trendsStartDate = '',
    trendsEndDate = '',
    onTrendsStartDateChange,
    onTrendsEndDateChange,
    onClose,
    embedded = false,
}) => {
    const theme = useTheme()

    const hasInvalidDateRange =
        !!trendsStartDate && !!trendsEndDate && trendsStartDate > trendsEndDate

    const containerStyle: React.CSSProperties = embedded
        ? {
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              backgroundColor: theme.palette.grey[100],
              padding: theme.spacing(2),
          }
        : {
              position: 'relative',
              flex: 1,
              overflow: 'auto',
              backgroundColor: theme.palette.grey[100],
              boxShadow: `2px -3px 8px ${theme.palette.grey[300]}`,
              padding: theme.spacing(2),
          }

    const totals = useMemo(() => {
        let income = 0
        let expenses = 0
        projects.forEach((p) => {
            income += toNumber(p.total_cost)
            expenses += toNumber(p.total_sub_payout)
        })
        const profit = income - expenses
        const marginPct = income > 0 ? Math.round((profit / income) * 100) : 0
        return { income, expenses, profit, marginPct }
    }, [projects])

    const chartData = useMemo(() => {
        const sorted = [...projects].sort(
            (a, b) => toNumber(b.total_cost) - toNumber(a.total_cost),
        )
        const top = sorted.slice(0, MAX_CHART_PROJECTS)
        const rest = sorted.slice(MAX_CHART_PROJECTS)

        const rows = top.map((p) => ({
            label: projectLabel(p),
            income: toNumber(p.total_cost),
            expenses: toNumber(p.total_sub_payout),
        }))

        if (rest.length > 0) {
            rows.push({
                label: `Other (${rest.length} projects)`,
                income: rest.reduce((s, p) => s + toNumber(p.total_cost), 0),
                expenses: rest.reduce(
                    (s, p) => s + toNumber(p.total_sub_payout),
                    0,
                ),
            })
        }

        return rows
    }, [projects])

    const trendsChartData = useMemo(() => {
        return Object.entries(trends)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([monthKey, values]) => {
                const [year, monthNum] = monthKey.split('-').map(Number)
                const date = new Date(year, monthNum - 1)
                return {
                    label: date.toLocaleDateString('en-US', {
                        month: 'short',
                        year: 'numeric',
                    }),
                    income: toNumber(values.income),
                    expenses: toNumber(values.expenses),
                }
            })
    }, [trends])

    const lineOptions: ApexOptions = useMemo(
        () => ({
            chart: {
                type: 'line',
                toolbar: { show: false },
                redrawOnParentResize: true,
            },
            xaxis: {
                categories: trendsChartData.map((r) => r.label),
                labels: {
                    style: { fontSize: '12px' },
                },
            },
            yaxis: {
                labels: {
                    formatter: (val: number) => formatCurrency(Number(val)),
                    style: { fontSize: '12px' },
                },
            },
            colors: [theme.palette.success.main, theme.palette.warning.main],
            stroke: {
                width: 3,
                curve: 'straight',
            },
            markers: {
                size: 4,
                hover: { size: 6 },
            },
            legend: {
                position: 'top',
                horizontalAlign: 'right',
                fontSize: '13px',
            },
            dataLabels: { enabled: false },
            tooltip: {
                y: { formatter: (val: number) => formatCurrency(val) },
            },
        }),
        [trendsChartData, theme],
    )

    const lineSeries = useMemo(
        () => [
            {
                name: 'Income',
                data: trendsChartData.map((r) => r.income),
            },
            {
                name: 'Expenses',
                data: trendsChartData.map((r) => r.expenses),
            },
        ],
        [trendsChartData],
    )

    const barOptions: ApexOptions = useMemo(
        () => ({
            chart: {
                type: 'bar',
                toolbar: { show: false },
                redrawOnParentResize: true,
            },
            plotOptions: {
                bar: {
                    horizontal: true,
                    barHeight: '70%',
                },
            },
            xaxis: {
                categories: chartData.map((r) => r.label),
                labels: {
                    formatter: (val: string) => formatCurrency(Number(val)),
                    style: { fontSize: '12px' },
                },
            },
            yaxis: {
                labels: {
                    style: { fontSize: '12px' },
                    maxWidth: 180,
                },
            },
            colors: [theme.palette.success.main, theme.palette.warning.main],
            legend: {
                position: 'top',
                horizontalAlign: 'right',
                fontSize: '13px',
            },
            dataLabels: { enabled: false },
            tooltip: {
                y: { formatter: (val: number) => formatCurrency(val) },
            },
        }),
        [chartData, theme],
    )

    const barSeries = useMemo(
        () => [
            {
                name: 'Income',
                data: chartData.map((r) => r.income),
            },
            {
                name: 'Expenses',
                data: chartData.map((r) => r.expenses),
            },
        ],
        [chartData],
    )

    if (loading) {
        return (
            <div
                style={{
                    ...containerStyle,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {!embedded && onClose && (
                    <IconButton
                        onClick={onClose}
                        style={{ position: 'absolute', top: 12, right: 12 }}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                )}
                <CircularProgress
                    size={48}
                    style={{ color: theme.palette.primary.main }}
                />
                <Typography
                    variant="subtitle1"
                    color="textSecondary"
                    style={{ marginTop: theme.spacing(2) }}
                >
                    Loading profit analytics...
                </Typography>
            </div>
        )
    }

    if (projects.length === 0) {
        return (
            <div
                style={{
                    ...containerStyle,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {!embedded && onClose && (
                    <IconButton
                        onClick={onClose}
                        style={{ position: 'absolute', top: 12, right: 12 }}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                )}
                <AssessmentIcon
                    style={{
                        fontSize: 64,
                        color: theme.palette.grey[400],
                        marginBottom: theme.spacing(2),
                    }}
                />
                <Typography variant="h6" color="textSecondary">
                    No project data to visualize
                </Typography>
            </div>
        )
    }

    const profitColor =
        totals.profit >= 0
            ? theme.palette.success.main
            : theme.palette.error.main

    return (
        <div style={containerStyle}>
            {!embedded && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: theme.spacing(2),
                    }}
                >
                    <Typography variant="h6">
                        Profit Analytics
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            component="span"
                            style={{ marginLeft: theme.spacing(1) }}
                        >
                            ({projects.length} projects)
                        </Typography>
                    </Typography>
                    {onClose && (
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    )}
                </div>
            )}

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: theme.spacing(2),
                    marginBottom: theme.spacing(2),
                }}
            >
                <MetricCard
                    label="Income"
                    value={formatCurrency(totals.income)}
                    color={theme.palette.success.main}
                />
                <MetricCard
                    label="Expenses"
                    value={formatCurrency(totals.expenses)}
                    color={theme.palette.warning.main}
                />
                <MetricCard
                    label="Profit"
                    value={formatCurrency(totals.profit)}
                    color={profitColor}
                />
                <MetricCard
                    label="Margin %"
                    value={`${totals.marginPct}%`}
                    color={profitColor}
                />
            </div>

            <div
                style={{
                    ...chartCardStyle(theme),
                    marginBottom: theme.spacing(2),
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                        gap: theme.spacing(2),
                        marginBottom: theme.spacing(1),
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                        Income vs Expenses Over Time
                    </Typography>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: theme.spacing(1.5),
                            flexWrap: 'wrap',
                        }}
                    >
                        <TextField
                            label="Start date"
                            type="date"
                            value={trendsStartDate}
                            onChange={(e) =>
                                onTrendsStartDateChange?.(e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            size="small"
                            style={{ width: 160 }}
                        />
                        <TextField
                            label="End date"
                            type="date"
                            value={trendsEndDate}
                            onChange={(e) =>
                                onTrendsEndDateChange?.(e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            size="small"
                            style={{ width: 160 }}
                            inputProps={{
                                min: trendsStartDate || undefined,
                            }}
                        />
                    </div>
                </div>
                {hasInvalidDateRange ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 280,
                        }}
                    >
                        <Typography variant="body2" color="error">
                            Start date must be on or before end date
                        </Typography>
                    </div>
                ) : trendsLoading ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 280,
                        }}
                    >
                        <CircularProgress
                            size={40}
                            style={{ color: theme.palette.primary.main }}
                        />
                    </div>
                ) : trendsChartData.length === 0 ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 280,
                        }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            No completed work orders to show trends
                        </Typography>
                    </div>
                ) : (
                    <ReactApexChart
                        options={lineOptions}
                        series={lineSeries}
                        type="line"
                        height={350}
                    />
                )}
            </div>

            <div style={chartCardStyle(theme)}>
                <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 600, marginBottom: theme.spacing(1) }}
                >
                    Income vs Expenses by Project
                </Typography>
                <ReactApexChart
                    options={barOptions}
                    series={barSeries}
                    type="bar"
                    height={Math.max(280, chartData.length * 48)}
                />
            </div>
        </div>
    )
}
